/* eslint-disable max-lines */
import { useRouter } from 'next/router'
import { isIOS } from 'react-device-detect'

import { generatePath, getUrlWithCombinedQueryParam, getUrlWithReplacedPathParam, isNullish } from '@local/utils'

import { gql } from '@web/gql'
import { UseGoToPlaceDetail_PlaceFragment, UserReviewGroupsSort } from '@web/gql/graphql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { isInternalUrl, movePage, openNewWindow, replacePage } from '@web/hooks/useNavigation'
import { BASE_PATH, messages } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import routes from '@web/libs/constants/routes'
import {
  Compare,
  compareVersion,
  goToNaverClipEditor,
  goToNaverClipViewer,
  mapsVersions,
  shouldOpenSaveMapSDK,
} from '@web/libs/entry.helper'
import { clearMobileCacheStorage, getHashedIdnoForMapAppBridge } from '@web/libs/utils'
import {
  reviewTabFilterStateVar,
  reviewTabPageOwnerIdVar,
} from '@web/pages/[objectId]/review/components/ReviewTab/filter.state'
import { resetCheckInListExpanded } from '@web/pages/checkin/index.state'
import { PathParam, QueryParam } from '@web/types'

type ROUTE = typeof routes
type ROUTE_KEYS = keyof typeof routes | keyof typeof links

type Option<Path extends string> = {
  params: Record<PathParam<Path>, string>
  query?: Partial<Record<QueryParam<Path>, string>>
  extraQuery?: Record<string, string>
  openWindow?: boolean
  replace?: boolean
}

type GoToArguments<URL extends string> = [URL, Omit<Option<URL>, 'params'>?]
type GoToArgumentsRequiredParams<URL extends string> = [URL, Option<URL>]

export const useGoTo = () => {
  const {
    isFromMapsApp,
    isMobile,
    hasNaverClipEditor,
    hasNaverClipViewer,
    mapAppVersion,
    isFromOpenMain,
    isFromMapsAppBottomSheet,
    mapsReferrer,
    isAndroid,
  } = useGlobalStaticStateContext()

  const router = useRouter()
  const isFromTimelineList = router.pathname === routes.TIMELINE_LIST

  const goTo = <URL extends ROUTE[ROUTE_KEYS]>(
    ...args: PathParam<URL> extends never ? GoToArguments<URL> : GoToArgumentsRequiredParams<URL>
  ) => {
    const [route, option] = args

    let url: string = route

    if (option && 'params' in option) {
      url = getUrlWithReplacedPathParam(url, option.params)
    }

    url = getUrlWithCombinedQueryParam(url, { ...option?.query, ...option?.extraQuery })

    let openWindow = option?.openWindow

    // openWindow가 사용처에서 manual하게 설정되어 있지 않고 외부 URL 이동인경우 새창 열기
    if (isNullish(openWindow) && !isInternalUrl(url)) {
      openWindow = true
    }

    if (isFromMapsAppBottomSheet) {
      openWindow = true
    }

    if (openWindow && isFromMapsApp && isAndroid) {
      // 안드로이드 지도앱일 경우 - 새로운 윈도우로 열고 싶을 땐 `target=new` 파라미터를 붙여주어야함.
      // https://oss.navercorp.com/Maps/android_navermap_v5/issues/14475
      url = getUrlWithCombinedQueryParam(url, { target: 'new' })
      openNewWindow(url)
      return
    }

    if (openWindow || (url.includes('nmap') && isFromMapsApp) || isFromOpenMain) {
      openNewWindow(url)
      return
    }

    if (option?.replace) {
      replacePage(url)
      return
    }

    movePage(url)
  }

  const goToLogin = ({ url, showAlert, replace }: GoLoginOption = {}) => {
    if (showAlert) alert(messages.LOGIN_NEED_ALERT)
    const currentUrl = window.location.href
    return goTo(routes.LOGIN, { query: { url: url ?? currentUrl }, replace })
  }

  const goToLogout = ({ url, replace }: Pick<GoLoginOption, 'url' | 'replace'> = {}) => {
    clearMobileCacheStorage()
    const currentUrl = window.location.href
    return goTo(routes.LOGOUT, { query: { url: url ?? currentUrl }, replace })
  }

  /** 타임라인 상세에선 새창열기 적용 */
  const goToReviewFeed = ({
    authorId,
    reviewId,
    openWindow,
    replace,
  }: {
    authorId: string
    reviewId?: string
    openWindow?: boolean
    replace?: boolean
  }) => {
    goTo(routes.REVIEW_FEED, {
      params: { userId: authorId },
      query: {
        reviewId: reviewId,
      },
      openWindow: isFromTimelineList ?? openWindow,
      replace,
    })
  }

  const goToClipNew = (serviceData: string) => {
    if (hasNaverClipEditor) {
      return goToNaverClipEditor(
        JSON.stringify({
          consumerId: 'myplace',
          type: 'new',
          serviceData,
        }),
      )
    }
    alert('네이버앱에서 실행해주세요.')
    return null
  }

  const goToClipEdit = (body: string) => {
    if (hasNaverClipEditor) {
      return goToNaverClipEditor(
        JSON.stringify({
          consumerId: 'myplace',
          type: 'modify',
          body,
        }),
      )
    }
    alert('네이버앱에서 실행해주세요.')
    return null
  }

  const openClipViewer = (videoId: string, recId: string) => {
    if (hasNaverClipViewer) {
      return goToNaverClipViewer(
        JSON.stringify({
          seedMediaId: videoId,
          mediaType: 'VOD',
          serviceType: 'MYPLACE',
          recType: 'MYPLACE',
          recId,
          panelType: 'vs_myplace',
          entryPoint: window?.location.href,
          clickCode: 'cpr.clipvideo',
          enableReverse: true,
        }),
      )
    }
    alert('네이버앱에서 실행해주세요.')
    return null
  }

  const goToBooking = (bookingId?: string) => {
    const extraQuery = mapsReferrer ? { referer: mapsReferrer } : undefined
    if (bookingId) {
      return goTo(routes.BOOKING_DETAIL, {
        params: { bookingId },
        extraQuery,
      })
    }
    return goTo(routes.BOOKING_MY, { extraQuery })
  }

  const goToOrder = (orderId?: string) => {
    const extraQuery = mapsReferrer ? { referer: mapsReferrer } : undefined
    if (orderId) {
      return goTo(routes.ORDER_DETAIL, {
        params: { orderId },
        extraQuery,
      })
    }
    return goTo(routes.ORDER_MY, { extraQuery })
  }

  const goToFeed = ({ isNewWindow = false }: { isNewWindow?: boolean }) => {
    const query = { napp: isFromOpenMain ? 'mysection' : '', referer: isFromMapsApp ? isFromMapsApp : '' }

    return goTo(routes.HOME_FEED, {
      openWindow: isNewWindow,
      query,
    })
  }

  const goToPlaceTab = (idno: string) => {
    const hashedIdno = getHashedIdnoForMapAppBridge(idno)
    if (shouldOpenSaveMapSDK(navigator.userAgent)) return openNewWindow('nmap://map?tab=bookmark')

    if (isMobile)
      return openNewWindow(
        `${links.MAP_APP_LINK}?app=Y&version=2&appTargetPage=map&tab=bookmark&userId=${hashedIdno}&appSchemeName=nmap&fallbackUrl=https://pages.map.naver.com/save-pages/web/all-list`,
      )

    return openNewWindow(`${links.PC_MAP}/favorite/myPlace`)
  }

  const goToReviewTabByViewCount = (userId: string) => {
    reviewTabPageOwnerIdVar(userId)
    reviewTabFilterStateVar.sort(UserReviewGroupsSort.ViewCountDesc)
    goTo(routes.HOME_REVIEW, { params: { userId } })
  }

  const goToCheckIn = ({ extraQuery, replace }: { extraQuery?: Record<string, string>; replace?: boolean } = {}) => {
    if (!isFromOpenMain) {
      resetCheckInListExpanded()
    }
    goTo(routes.CHECKIN, { extraQuery, replace })
  }

  const goToDirectionLandingUrl = (directionLandingUrl?: { appScheme?: string | null; webUrl?: string | null }) => {
    if (!directionLandingUrl) {
      return undefined
    }

    if (isFromMapsApp && directionLandingUrl.appScheme) {
      return openNewWindow(directionLandingUrl.appScheme)
    }

    if (directionLandingUrl.webUrl) {
      return movePage(directionLandingUrl.webUrl)
    }

    return undefined
  }

  const goToPlaceDetail = (place: UseGoToPlaceDetail_PlaceFragment) => {
    if (!place.isAvailable || place.isBusinessPlace) {
      return
    }

    if (place.naverHotelId) {
      openNewWindow(generatePath(links.TRAVEL_HOTEL_DETAIL, { hotelId: place.naverHotelId }))
      return
    }

    // IOS 일때 지도앱 5.33.0 버전 이상일 때 인앱 스킴 적용
    if (isIOS && mapAppVersion && compareVersion(mapsVersions.MAPS_PLACE_DETAIL, mapAppVersion) >= Compare.EQUAL) {
      window.location.href = `inapp://place?id=${place.id}&mode=half&appname=m.place.naver.com/my`
      return
    }

    if (mapAppVersion && compareVersion(mapsVersions.MY_TAB_NATIVE_SDK, mapAppVersion) >= Compare.EQUAL) {
      window.location.href = `nmap://place?id=${place.id}&mode=half&appname=m.place.naver.com/my`
      return
    }

    goTo(routes.PLACE_DETAIL, { params: { placeId: place.id }, openWindow: true })
  }

  return {
    goTo,
    goToLogin,
    goToLogout,
    goToReviewFeed,
    goToBooking,
    goToOrder,
    goToFeed,
    goToPlaceTab,
    goToPlaceDetail,
    goToReviewTabByViewCount,
    goToCheckIn,
    goToClipNew,
    goToClipEdit,
    goToDirectionLandingUrl,
    openClipViewer,
  }
}

useGoTo.fragments = gql(`
  fragment UseGoToReviewEdit_ReviewGroup on ReviewGroup {
    id
    visit {
      ...UseGoToReviewNew_Visit
    }
  }
  fragment UseGoToReviewNew_Visit on Visit {
    id
    mainProofType
  }
  fragment UseGoToPlaceDetail_Place on Place {
    id
    naverHotelId
    isAvailable
    isBusinessPlace
  }
`)

export const isCSRUrl = (url: string) => {
  return url.startsWith('/') && !url.startsWith(BASE_PATH)
}

interface GoLoginOption {
  url?: string
  showAlert?: boolean
  replace?: boolean
}

// useNavigation으로 통일되면 제거예정
export { movePage, openNewWindow, replacePage }
